import { sprintf } from 'sprintf-js';
import styled from 'styled-components';

import { ApiListingMini as OrgListingMini } from 'api/types/ApiListingMini';
import { Box } from 'components/Box';
import { ArrowLink, RouterLink } from 'components/Link';
import { BodySmall } from 'components/Text/BodySmall';
import { H4 } from 'components/Text/H4';
import { ToggleHelperContainer } from 'containers/ToggleHelperContainer';
import { colors, cssBreakpoints, fontWeights } from 'theme/theme';
import { getCityStateString } from 'utils/address/getCityStateString';
import { LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_TITLE } from 'utils/constants/general/listingTypePluralHumanNameMapTitle';
import { humanDate } from 'utils/date';

const MAX_LISTINGS = 10;
const StyledTable = styled.table`
  width: 100%;
  text-align: left;
  border-spacing: 10px 11px;
  margin-top: -11px;
  table-layout: fixed;
  min-width: 100%;
  overflow: hidden;
  td {
    color: ${colors.lightContentGrey};
  }
  th {
    font-size: 18px;
    color: ${colors.mediumContentGrey};
    padding-top: 10px;
    padding-bottom: 32px;
    font-weight: ${fontWeights.semiBold};
    border-top: solid 1px ${colors.elementGrey};
  }
  td,
  th {
    padding: 0;
    max-width: 28vw;
    width: 28vw;
  }

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    border-spacing: 40px 11px;

    th {
      font-size: 18px;
    }

    td,
    th {
      padding: 0;
      max-width: 30vw;
      &:nth-child(2) {
        border-spacing: 40px 11px;
        width: 180px;
      }
      &:nth-child(3) {
        width: 160px;
      }
    }
  }
`;

function Listings({
  listingType,
  listings,
  ...props
}: {
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP';
  listings: Array<OrgListingMini>;
}) {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Box mx={['-10px', '-10px', '-10px', '-40px']} {...props}>
      <StyledTable>
        <thead>
          <tr>
            <H4 tag="th" pt="11px" pb="28px">
              {LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_TITLE[listingType]}
            </H4>
            <H4 tag="th" pt="11px" pb="28px">
              {getText('Location')}
            </H4>
            <H4 tag="th" pt="11px" pb="28px">
              {getText('Date Posted')}
            </H4>
          </tr>
        </thead>
        <tbody>
          {listings.map((listing) => (
            <tr key={listing.id}>
              <BodySmall tag="td" pb="7px">
                <RouterLink to={listing.url[CURRENT_LOCALE]}>
                  {listing.name}
                </RouterLink>
              </BodySmall>
              <BodySmall tag="td">
                {!listing.address
                  ? 'Online/Virtual'
                  : getCityStateString(listing.address)}
              </BodySmall>
              <BodySmall tag="td">
                {humanDate(listing.firstPublished)}
              </BodySmall>
            </tr>
          ))}
        </tbody>
      </StyledTable>
    </Box>
  );
}

type Props = {
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP';
  listings: Array<OrgListingMini>;
};

export function TruncatedListings({ listingType, listings }: Props) {
  const hasMore = listings.length > MAX_LISTINGS;

  if (hasMore) {
    return (
      <ToggleHelperContainer>
        {(showMore, toggleShowMore) => (
          <>
            <div style={{ marginBottom: 10 }}>
              <Listings
                listingType={listingType}
                listings={showMore ? listings : listings.slice(0, MAX_LISTINGS)}
              />
            </div>

            <ArrowLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                toggleShowMore();
              }}
              open={showMore}
            >
              {showMore
                ? getText('View less')
                : sprintf(getText('View %(count)s more'), {
                    count: listings.length - MAX_LISTINGS,
                  })}
            </ArrowLink>
          </>
        )}
      </ToggleHelperContainer>
    );
  }

  return <Listings listingType={listingType} listings={listings} />;
}
